import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import ServicesPage from "@/pages/ServicesPage";
import PortfolioPage from "@/pages/PortfolioPage";
import VendorsPage from "@/pages/VendorsPage";
import ContactPage from "@/pages/ContactPage";
import TestimonialsPage from "@/pages/TestimonialsPage";

// Client Portal
import ClientLogin from "@/pages/client/ClientLogin";
import ClientDashboard from "@/pages/client/ClientDashboard";

// Admin
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";

// Components
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import IntroAnimation from "@/components/IntroAnimation";
import PageTransition from "@/components/PageTransition";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = '/api';

// Auth context helpers
export const getAuthToken = () => localStorage.getItem('adminToken');
export const setAuthToken = (token) => localStorage.setItem('adminToken', token);
export const clearAuthToken = () => localStorage.removeItem('adminToken');

export const getClientToken = () => localStorage.getItem('clientToken');
export const setClientToken = (token) => localStorage.setItem('clientToken', token);
export const clearClientToken = () => localStorage.removeItem('clientToken');

// Global 401 handler — redirect to login on expired/invalid tokens
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const path = window.location.pathname;
      if (path.startsWith('/admin')) {
        clearAuthToken();
        window.location.href = '/admin/login';
      } else if (path.startsWith('/portal') || path.startsWith('/client')) {
        clearClientToken();
        window.location.href = '/portal/login';
      }
    }
    return Promise.reject(error);
  }
);

// Protected Route components
const ProtectedAdminRoute = ({ children }) => {
  const token = getAuthToken();
  if (!token) {
    return <Navigate to="/admin/login" replace />;
  }
  return children;
};

const ProtectedClientRoute = ({ children }) => {
  const token = getClientToken();
  if (!token) {
    return <Navigate to="/portal/login" replace />;
  }
  return children;
};

// Public layout wrapper
const PublicLayout = ({ children }) => {
  return (
    <>
      <Navbar />
      <main>{children}</main>
      <Footer />
      <ScrollToTop />
    </>
  );
};

function App() {
  const [showIntro, setShowIntro] = useState(false);

  useEffect(() => {
    // Seed database if not done yet
    // Seed database only once ever (not per session)
    const alreadySeeded = localStorage.getItem('dbSeeded');
    if (!alreadySeeded) {
      axios.post(`${API}/seed`).then(() => {
        localStorage.setItem('dbSeeded', 'true');
      }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    // Only show intro on public pages, not admin or portal
    const isAdminOrPortal = window.location.pathname.startsWith('/admin') || window.location.pathname.startsWith('/portal');
    const introShown = sessionStorage.getItem('introShown');
    if (!introShown && !isAdminOrPortal) {
      setShowIntro(true);
    }
  }, []);

  const handleIntroComplete = () => {
    sessionStorage.setItem('introShown', 'true');
    setShowIntro(false);
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        
        {showIntro && <IntroAnimation onComplete={handleIntroComplete} />}
        
        <PageTransition>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
            <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
            <Route path="/services" element={<PublicLayout><ServicesPage /></PublicLayout>} />
            <Route path="/portfolio" element={<PublicLayout><PortfolioPage /></PublicLayout>} />
            <Route path="/vendors" element={<PublicLayout><VendorsPage /></PublicLayout>} />
            <Route path="/testimonials" element={<PublicLayout><TestimonialsPage /></PublicLayout>} />
            <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
            
            {/* Client Portal Routes */}
            <Route path="/portal/login" element={<ClientLogin />} />
            <Route path="/portal/*" element={
              <ProtectedClientRoute>
                <ClientDashboard />
              </ProtectedClientRoute>
            } />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/*" element={
              <ProtectedAdminRoute>
                <AdminDashboard />
              </ProtectedAdminRoute>
            } />
          </Routes>
        </PageTransition>
      </BrowserRouter>
    </div>
  );
}

export default App;
